import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Tenant, User } from '@/types'
import { setAuthContext } from '@/internationalization/api-client'

interface SessionState {
  token: string | null
  user: User | null
  tenant: Tenant | null
  /**
   * What this person may do in the current workspace, or NULL while unknown.
   *
   * NULL IS NOT AN EMPTY SET and the distinction is the whole reason this is
   * nullable: `usePermissions` treats null as denied, so the UI renders its
   * locked-down state while the session is being established rather than
   * flashing an admin panel at a viewer on every reload. An empty ARRAY means
   * "asked, and they hold nothing", which looks the same on screen and is a
   * different fact.
   *
   * DELIBERATELY NOT PERSISTED. It is not a credential, but a stale copy read
   * out of localStorage before the session is re-established would be exactly
   * the flash this nullability exists to prevent -- and worse, a permission
   * since revoked would come back on next load.
   */
  permissions: string[] | null
  /**
   * What this DEPLOYMENT can do, or NULL while unknown.
   *
   * Null for the same reason `permissions` is: unknown must render as absent,
   * never as present. A feature that flashes into view and then disappears is
   * worse than one that appears a moment late.
   *
   * NOT PERSISTED, and not for the staleness reason -- a capability is a fact
   * about the server, so a cached copy would survive a deploy that removed the
   * wiring and offer a button that has stopped working.
   */
  capabilities: Record<string, boolean> | null
  /**
   * Whether this caller is one of the three people the vocabulary feature is
   * hardcoded to on the server -- never a tenant permission, so it cannot be
   * derived from `permissions`. NULL while unknown, same convention as
   * `permissions`: the nav item must never flash into view before the real
   * answer arrives.
   */
  canAccessVocabulary: boolean | null
  /** False until the first refresh attempt has settled, so the guard can wait. */
  restored: boolean
  signIn: (payload: {
    token: string
    user: User
    tenant: Tenant
    permissions?: string[] | null
  }) => void
  signOut: () => void
  switchTenant: (tenant: Tenant) => void
  setPermissions: (permissions: string[] | null) => void
  setCapabilities: (capabilities: Record<string, boolean> | null) => void
  setCanAccessVocabulary: (canAccessVocabulary: boolean | null) => void
  markRestored: () => void
}

/**
 * Who is signed in and which tenant they are acting as.
 *
 * THE ACCESS TOKEN IS NEVER PERSISTED. It lives in memory only, and a reload
 * re-obtains one from the HttpOnly refresh cookie via POST /auth/refresh. It
 * used to sit in localStorage, where any script on the page can read it, and
 * where it also went stale: Cognito access tokens last an hour, nothing ever
 * called /auth/refresh, and the session simply started 401-ing with no
 * recovery.
 *
 * The tenant id IS persisted, because it is not a credential -- it is which
 * workspace you were last looking at, and the API re-proves membership on every
 * request anyway. Tenant is part of the session rather than a URL parameter:
 * one wrong id in a shared link would ask for another customer's meetings.
 */
export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      tenant: null,
      permissions: null,
      capabilities: null,
      canAccessVocabulary: null,
      restored: false,
      signIn: ({ token, user, tenant, permissions }) => {
        setAuthContext({ token, tenantId: tenant.id })
        set({
          token,
          user,
          tenant,
          permissions: permissions ?? null,
          restored: true,
        })
      },
      signOut: () => {
        setAuthContext({ token: null, tenantId: null })
        set({
          token: null,
          user: null,
          tenant: null,
          permissions: null,
          capabilities: null,
          canAccessVocabulary: null,
          restored: true,
        })
      },
      switchTenant: (tenant) => {
        set((state) => {
          setAuthContext({ token: state.token, tenantId: tenant.id })
          // CLEARED, NOT CARRIED OVER. One person can hold different roles in
          // two workspaces -- a consultant who is an admin at one customer and
          // a viewer at another -- so keeping the old set would show them the
          // wrong workspace's menu, in the more permissive direction.
          // Capabilities are a property of the DEPLOYMENT and not of the
          // workspace, so they survive a switch. `canAccessVocabulary` is a
          // fact about the person that does not vary by workspace either, but
          // it is still re-fetched with `permissions` (both come off the same
          // `/authorization/me` response) rather than assumed unchanged.
          return { tenant, permissions: null, canAccessVocabulary: null }
        })
      },
      setPermissions: (permissions) => set({ permissions }),
      setCapabilities: (capabilities) => set({ capabilities }),
      setCanAccessVocabulary: (canAccessVocabulary) => set({ canAccessVocabulary }),
      markRestored: () => set({ restored: true }),
    }),
    {
      name: 'sabha.session',
      // Deliberately narrow. Anything added here is readable by any script that
      // reaches the page, so it must never include a credential.
      partialize: (state) => ({ tenant: state.tenant, user: state.user }),
      onRehydrateStorage: () => (state) => {
        if (state?.tenant) {
          setAuthContext({ token: null, tenantId: state.tenant.id })
        }
      },
    },
  ),
)
